import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AdminFormPage,
  useAdminFormPersistence,
} from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import { useV2GetPaymentTermQuery } from "@generated";
import { PaymentTermForm } from "../PaymentTermForm/PaymentTermForm";
import { usePaymentTermMutations } from "../api";
import { emptyPaymentTermForm, nextTermRowKey } from "../types";
import type { PaymentTermFormValues, TermType } from "../types";

const FORM_ID = "payment-term-form";

export const Edit = (): JSX.Element => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { update } = usePaymentTermMutations();
  const { data, isLoading, refetch } = useV2GetPaymentTermQuery(
    { id: Number(id) },
    { refetchOnMountOrArgChange: true }
  );
  const { toast, setToast, savedInitial, formKey, markSaved } =
    useAdminFormPersistence<PaymentTermFormValues>();
  const [submitting, setSubmitting] = useState(false);

  const loadedInitial = useMemo<PaymentTermFormValues>(() => {
    const attrs = data?.data?.attributes;
    if (!attrs) return emptyPaymentTermForm;
    // terms[] members are snake_case on the wire.
    const rawTerms = (attrs.terms ?? []) as Array<{
      id?: number;
      description?: string;
      percentage_due?: number;
      term_type?: TermType;
      phase_id?: number;
    }>;
    const termType: TermType = rawTerms[0]?.term_type ?? "percent";
    return {
      name: attrs.name ?? "",
      pricingModel: attrs["pricing-model"] ?? emptyPaymentTermForm.pricingModel,
      rounding: attrs.rounding ?? emptyPaymentTermForm.rounding,
      default: !!attrs.default,
      includeExpenseInRevenue: !!attrs["include-expense-in-revenue"],
      includeExpenseInCost: !!attrs["include-expense-in-cost"],
      includeMaterials: !!attrs["include-materials"],
      travelLimit: !!attrs["travel-limit"],
      termType,
      terms: rawTerms.map((t) => ({
        key: nextTermRowKey(),
        id: t.id,
        description: t.description ?? "",
        percentageDue: t.percentage_due ?? null,
        phaseId: t.phase_id ?? null,
      })),
    };
  }, [data]);

  if (isLoading) return <ScopeStackSpinner />;

  const initialValues = savedInitial ?? loadedInitial;

  const handleSubmit = async (values: PaymentTermFormValues) => {
    setSubmitting(true);
    try {
      await update(id, values);
      markSaved(values, `'${values.name}' saved`);
      // The generated API defines no cache tags, so the update can't
      // invalidate the cached GET. Refetch so the cache holds the
      // server-fresh record for the next time Edit is opened.
      refetch();
    } catch (err) {
      console.error("Failed to update payment term", err);
      setToast({ variant: "danger", text: "Could not save payment term." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <AdminFormPage
        title="Edit Payment Term"
        formId={FORM_ID}
        onCancel={() => navigate("../..", { relative: "path" })}
        isSubmitting={submitting}
      >
        <PaymentTermForm
          key={formKey}
          id={FORM_ID}
          initialValues={initialValues}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          onInvalid={(text) => setToast({ variant: "danger", text })}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default Edit;
